/**
 * 生徒用フィードバック確認機能
 * 軽量・分析データ付き
 */

import { ref, reactive } from 'vue'
import { supabase } from '../supabaseClient'
import { track, generatePlaybackId, getSessionId } from '../utils/feedbackTracker.js'

export function useFeedbackCheck() {
  // 状態管理
  const isChecking = ref(false)
  const showFeedbackModal = ref(false)
  const currentFeedbackDay = ref(null)
  const currentFeedbackType = ref('practice')
  const currentLearnerId = ref(null) // 分析イベント(track)に付与するため、どの子の再生かを保持
  
  // フィードバックデータ
  const feedbackData = reactive({
    signedUrl: null,
    assignmentText: '',
    totalDuration: 0,
    fileSize: 0,
    version: 1,
    expiresAt: null
  })
  
  // 再生トラッキング
  const playbackState = reactive({
    playbackId: null,
    startTime: null,
    currentPosition: 0,
    isPlaying: false,
    endedSent: false // 二重送信防止フラグ
  })
  
  /**
   * フィードバック確認（1本化API）
   * @param {number|string} dayOrSubmissionId - Day番号 または submissionId
   * @param {string} type - 'practice' | 'assignment' | null（submissionId指定時）
   * @param {boolean} isDirectSubmission - submissionIdを直接指定する場合はtrue
   * @param {string|null} learnerId - 兄弟対応: 対象の子の learner_id（省略時は従来どおり）
   */
  const checkFeedback = async (dayOrSubmissionId, type = 'practice', isDirectSubmission = false, learnerId = null) => {
    try {
      isChecking.value = true
      currentLearnerId.value = learnerId

      // 認証トークン取得
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token || ''
      
      let response
      
      if (isDirectSubmission) {
        // submissionId を直接指定する場合
        const submissionId = dayOrSubmissionId
        console.log('📝 FB確認開始（submissionId指定）:', { submissionId })
        
        // submissionId指定のAPI呼び出し
        response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/feedback-by-submission?submissionId=${submissionId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      } else {
        // day/type で検索する場合（既存）
        const day = dayOrSubmissionId
        console.log('📝 FB確認開始:', { day, type })
        
        // イベント送信: ボタンクリック（fire-and-forget）
        track('clicked', {
          day: parseInt(day),
          type,
          learner_id: learnerId,
          session_id: getSessionId()
        })
        
        // 1本化API呼び出し（兄弟対応: learnerId があれば付与）
        const learnerQ = learnerId ? `&learnerId=${encodeURIComponent(learnerId)}` : ''
        response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/feedback?day=${day}&type=${type}${learnerQ}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      }
      
      // エラーハンドリング：詳細なエラーメッセージ
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        
        if (response.status === 404 && errorData.code === 'NO_FEEDBACK') {
          // フィードバック未作成の場合
          if (confirm('まだフィードバックのファイルが作成されていません。\n\n再読込しますか？')) {
            window.location.reload()
          }
          return
        } else if (response.status === 403) {
          // 権限エラー
          alert('アクセス権限がありません')
          return
        } else if (response.status === 502 && errorData.code === 'SIGN_URL_ERROR') {
          // 署名URL生成エラー
          alert('音声ファイルの準備に失敗しました。\n時間をおいて再度お試しください。')
          return
        } else {
          throw new Error(`API Error: ${response.status}`)
        }
      }
      
      const result = await response.json()
      console.log('📝 FB確認結果:', result)
      
      if (result.exists) {
        // フィードバックが存在する場合
        feedbackData.signedUrl = result.signedUrl
        feedbackData.assignmentText = result.assignmentText
        feedbackData.totalDuration = result.totalDuration || 0
        feedbackData.fileSize = result.fileSize || 0
        feedbackData.version = result.version || 1
        feedbackData.expiresAt = Date.now() + (result.ttlSec * 1000)
        
        // submissionId指定の場合は day/type を result から取得
        const day = isDirectSubmission ? result.day : dayOrSubmissionId
        const actualType = isDirectSubmission ? result.type : type
        
        currentFeedbackDay.value = parseInt(day)
        currentFeedbackType.value = actualType
        
        // モーダル表示
        showFeedbackModal.value = true
        
        // イベント送信: モーダル表示（fire-and-forget）
        track('modal_opened', {
          day: parseInt(day),
          type: actualType,
          learner_id: currentLearnerId.value,
          total_ms: feedbackData.totalDuration * 1000,
          file_size: feedbackData.fileSize,
          version: feedbackData.version,
          session_id: getSessionId()
        })
        
        console.log('✅ FBモーダル表示')
        
      } else {
        // フィードバックが存在しない場合（200で返ってきた場合）
        alert(result.message || 'まだフィードバックされていません')
        
        // イベント送信: 存在しない（fire-and-forget）
        const day = isDirectSubmission ? result.day : dayOrSubmissionId
        const actualType = isDirectSubmission ? result.type : type
        
        track('not_available', {
          day: parseInt(day),
          type: actualType,
          learner_id: currentLearnerId.value,
          session_id: getSessionId()
        })
      }
      
    } catch (error) {
      console.error('❌ FB確認エラー:', error)
      alert(`フィードバック確認エラー: ${error.message}`)
    } finally {
      isChecking.value = false
    }
  }
  
  /**
   * 音声再生開始
   */
  const startPlayback = async () => {
    try {
      // プレイバックID生成
      playbackState.playbackId = generatePlaybackId()
      playbackState.startTime = Date.now()
      playbackState.isPlaying = true
      playbackState.endedSent = false // リセット
      
      console.log('🎵 FB再生開始:', playbackState.playbackId)
      
      // イベント送信: 再生開始（fire-and-forget）
      track('play_started', {
        day: currentFeedbackDay.value,
        type: currentFeedbackType.value,
        learner_id: currentLearnerId.value,
        playback_id: playbackState.playbackId,
        total_ms: feedbackData.totalDuration * 1000,
        session_id: getSessionId()
      })
      
    } catch (error) {
      console.error('❌ 再生開始トラッキングエラー:', error)
    }
  }
  
  /**
   * 音声再生終了
   */
  const endPlayback = async (currentTime = 0) => {
    try {
      if (!playbackState.playbackId || playbackState.endedSent) return
      
      const position_ms = Math.round(currentTime * 1000)
      playbackState.currentPosition = position_ms
      playbackState.isPlaying = false
      playbackState.endedSent = true // 二重送信防止
      
      console.log('🎵 FB再生終了:', { 
        playbackId: playbackState.playbackId, 
        position_ms 
      })
      
      // イベント送信: 再生終了（fire-and-forget）
      track('play_ended', {
        day: currentFeedbackDay.value,
        type: currentFeedbackType.value,
        learner_id: currentLearnerId.value,
        playback_id: playbackState.playbackId,
        position_ms,
        total_ms: feedbackData.totalDuration * 1000,
        session_id: getSessionId()
      })
      
    } catch (error) {
      console.error('❌ 再生終了トラッキングエラー:', error)
    }
  }
  
  /**
   * モーダルを閉じる
   */
  const closeFeedbackModal = async () => {
    try {
      // イベント送信: モーダル閉じる（fire-and-forget）
      track('modal_closed', {
        day: currentFeedbackDay.value,
        type: currentFeedbackType.value,
        learner_id: currentLearnerId.value,
        playback_id: playbackState.playbackId,
        position_ms: playbackState.currentPosition,
        session_id: getSessionId()
      })

      // 状態リセット
      showFeedbackModal.value = false
      currentFeedbackDay.value = null
      currentFeedbackType.value = 'practice'
      currentLearnerId.value = null
      feedbackData.signedUrl = null
      feedbackData.assignmentText = ''
      playbackState.playbackId = null
      playbackState.startTime = null
      playbackState.currentPosition = 0
      playbackState.isPlaying = false
      playbackState.endedSent = false // 二重送信防止フラグもリセット
      
      console.log('✅ FBモーダル閉じる')
      
    } catch (error) {
      console.error('❌ モーダル閉じるトラッキングエラー:', error)
    }
  }
  
  return {
    // 状態
    isChecking,
    showFeedbackModal,
    currentFeedbackDay,
    currentFeedbackType,
    currentLearnerId,
    feedbackData,
    playbackState,
    
    // メソッド
    checkFeedback,
    startPlayback,
    endPlayback,
    closeFeedbackModal
  }
}
